import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { useMutation } from '@apollo/client'
import {
  CreatePromoteRequestContractsContractVersionRequestDocument,
  JobCatalogTreeFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { ValidationError } from 'modules/core/lib/errors'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import { employeeUser } from 'modules/core/store/collections/users'
import type { FlashType } from 'modules/core/store/models/app'
import app from 'modules/core/store/models/app'

import Dates from 'modules/contracts/components/FlowPage/Steps/Dates'
import RoleLevelSalary from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary'
import { useNewAdditionalCompensationsFeatureEnabled } from 'modules/contracts/hooks/useNewAdditionalCompensationsFeatureEnabled'
import ContractFlow from 'modules/contracts/lib/ContractFlow'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { isGraphQLType } from 'modules/contracts/lib/types'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import { FlowPageContent } from '../FlowPage/_FlowPageContent'
import Footer from './_Footer'
import DatesStep from './lib/DatesStep'
import RoleLevelSalaryStep from './lib/RoleLevelSalaryStep'
import { translations as datesStepTranslations } from './lib/datesTranslations'
import { getPromoteFlowForm } from './lib/promoteFlowForm'
import { translations as roleLevelSalaryTranslations } from './lib/roleLevelSalaryTranslations'
import { translations as templateTranslations } from './lib/templateTranslations'

interface Props {
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
}

const PromoteFlowContent = ({ jobCatalogTrees, jobCatalogTreeFeatureEnabled }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    employee,
    contractVersion,
    compensations,
    additionalCompensations,
    contractVersionPolicy,
    legalEntityContractData,
  } = useContractFlow()
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const form = useLazyRef(() =>
    getPromoteFlowForm(contractVersion, employee, contractVersionPolicy, policyAuthorizations)
  ).current
  const onClose = () => {
    navigate(
      navigation.employees.id.contractVersions.fullPath({
        id: employee.id.toString(),
      })
    )
  }
  const [isCurrentStepCompleted, setIsCurrentStepCompleted] = React.useState(false)

  const promoteContractFlow = useLazyRef(
    () =>
      new ContractFlow([
        new DatesStep(
          (
            <Dates
              step={1}
              type="promote"
              form={form}
              translations={datesStepTranslations()}
              setIsCurrentStepCompleted={setIsCurrentStepCompleted}
            />
          )
        ),
        new RoleLevelSalaryStep(
          (
            <RoleLevelSalary
              step={2}
              type="promote"
              form={form}
              translations={roleLevelSalaryTranslations()}
              setIsCurrentStepCompleted={setIsCurrentStepCompleted}
              jobCatalogTrees={jobCatalogTrees}
              jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
            />
          )
        ),
      ])
  ).current
  const { company } = useCurrentSession()
  const isNewAdditionalCompensationsSupportEnabled = useNewAdditionalCompensationsFeatureEnabled({
    companyId: company.id,
  })

  const [createPromoteRequestContractsContractVersionRequest] = useMutation(
    CreatePromoteRequestContractsContractVersionRequestDocument
  )

  const showFlashMessage = (type: FlashType, message: string) => {
    app.setFlash({
      type,
      message,
    })
  }

  const onSubmit = () => {
    setIsSubmitting(true)
    const api = ApiClient()
    const data = form.serialized
    data.additional_compensations = compensations.map((compensation) => compensation.attributes)
    data.employee_id = employee.id

    if (isNewAdditionalCompensationsSupportEnabled) {
      return createPromoteRequestContractsContractVersionRequest({
        variables: {
          additionalCompensations: data.additional_compensations,
          newAdditionalCompensations: additionalCompensations?.map(({ tmpID: _, ...rest }) => {
            return {
              amount: rest.amountStrategy?.amount,
              amountStrategyType: rest.amountStrategyType,
              contractVersionId: rest.contractVersionId,
              description: rest.description,
              firstPaymentOn: rest.firstPaymentOn,
              payrollConceptId: rest.payrollConceptId,
              recurrence: rest.recurrence,
              upperLimit:
                rest.amountStrategy &&
                isGraphQLType(rest.amountStrategy, 'ContractsVariableAmountStrategy')
                  ? rest.amountStrategy?.upperLimit
                  : 0,
            }
          }),
          employeeId: employee.id,
          effectiveOn: form.serialized.effective_on,
          esJobDescription: form.serialized.es_job_description,
          jobCatalogLevelId: form.serialized.job_catalog_level_id,
          // TODO: Add the job catalog tree node uuid to the mutation DTO
          // once we have it in the contract version
          jobTitle: form.serialized.job_title,
          payrollPolicyId: form.serialized.payroll_policy_id,
          requestDetails: form.serialized.request_details,
          salaryAmount: form.serialized.salaryAmount,
          salaryFrequency: form.serialized.salaryFrequency,
        },
        onCompleted: (data) => {
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.promote.confirmation', {
              employeeFullName: employeeUser(employee).fullName(),
            })
          )
          data.contractsMutations.createPromoteRequestContractsContractVersionRequest
            ?.contractVersionRequest?.id &&
            navigate(
              navigation.employees.id.contractVersions.contractVersionId.fullPath({
                id: employee.id.toString(),
                contractVersionId:
                  data.contractsMutations.createPromoteRequestContractsContractVersionRequest?.contractVersionRequest?.id.toString(),
              })
            )
        },
        onError: (error) => {
          showFlashMessage('wrong', error.message)
          setIsSubmitting(false)
        },
      })
    } else {
      api
        .post(`/api/rest/contracts/contract_version_requests/create_promote_request`, data)
        .then((newContract) => {
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.promote.confirmation', {
              employeeFullName: employeeUser(employee).fullName(),
            })
          )
          navigate(
            navigation.employees.id.contractVersions.contractVersionId.fullPath({
              id: employee.id.toString(),
              contractVersionId: newContract.id.toString(),
            })
          )
        })
        .catch((e) => {
          if (e instanceof ValidationError) showFlashMessage('wrong', e.message)
          else showFlashMessage('wrong', i18n.t('contract_versions.flows.errors.unknown'))
        })
    }
  }

  return (
    <FlowPageContent
      translations={templateTranslations()}
      form={form}
      employee={employee}
      contractVersion={contractVersion}
      legalEntityContractData={legalEntityContractData}
      title={i18n.t('contract_versions.flows.promote.header.title', {
        employeeFullName: employeeUser(employee).fullName(),
      })}
      flow={promoteContractFlow}
      footerContent={<Footer flow={promoteContractFlow} employee={employee} />}
      isCurrentStepCompleted={isCurrentStepCompleted}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={() => onSubmit()}
    />
  )
}

export default observer(PromoteFlowContent)
